/**
 * Quote sharing.
 *
 * Two transports, deliberately:
 *
 *   1. The API — POST the quote, get a short link like /q/ab12cd34. Short
 *      enough to paste into an email, and the row can be looked up or revoked
 *      later.
 *   2. A URL fragment — the whole quote encoded into the link itself. Long, but
 *      it needs no server at all, so the feature still works when the app is
 *      deployed as a static bundle with no API behind it.
 *
 * The API is tried first and the fragment is the fallback, so sharing never
 * hard-fails just because the backend is not reachable.
 */
import { DEFAULT_QUOTE_STATE } from './pricing';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/** Only the fields that differ from defaults, so encoded links stay short. */
const diffFromDefaults = (state) => {
    const out = {};
    Object.keys(state).forEach(key => {
        const value = state[key];
        const base = DEFAULT_QUOTE_STATE[key];
        if (JSON.stringify(value) !== JSON.stringify(base)) out[key] = value;
    });
    return out;
};

/** base64url so the payload survives being pasted into a URL. */
const encode = (obj) => {
    const json = JSON.stringify(obj);
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const decode = (token) => {
    const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
};

/** Self-contained link that needs no backend. */
export const buildFallbackLink = (state, meta = {}) => {
    const token = encode({ s: diffFromDefaults(state), m: meta });
    return `${window.location.origin}/q/inline#${token}`;
};

/**
 * Persist a quote and return a shareable link.
 * Falls back to an encoded link when the API is unavailable.
 */
export const saveQuote = async (state, meta = {}) => {
    try {
        const response = await fetch(`${API_BASE}/quotes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state: diffFromDefaults(state), meta })
        });
        if (!response.ok) throw new Error(`API responded ${response.status}`);
        const data = await response.json();
        return {
            id: data.id,
            url: `${window.location.origin}/q/${data.id}`,
            persisted: true
        };
    } catch (error) {
        return {
            id: null,
            url: buildFallbackLink(state, meta),
            persisted: false,
            reason: error.message
        };
    }
};

/**
 * Resolve a quote for the customer-facing view.
 * `id` of "inline" means the payload is in the URL fragment.
 */
export const loadQuote = async (id, fragment = '') => {
    if (id === 'inline') {
        const token = (fragment || '').replace(/^#/, '');
        if (!token) throw new Error('This link is missing its quote data.');
        const parsed = decode(token);
        return { state: parsed.s || {}, meta: parsed.m || {}, persisted: false };
    }

    const response = await fetch(`${API_BASE}/quotes/${encodeURIComponent(id)}`);
    if (response.status === 404) throw new Error('This quote link has expired or was removed.');
    if (!response.ok) throw new Error(`Could not load the quote (${response.status}).`);
    const data = await response.json();
    return { state: data.state || {}, meta: data.meta || {}, persisted: true, createdAt: data.created_at };
};
